import { createReducer, on } from '@ngrx/store';
import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import {
	IDraft,
	IDraftFilter,
	IDraftPreview,
	IDraftVerdictResult,
} from './draft.interface';
import { DraftActions } from './draft.actions';
import { IResponseError } from '../../service/interface';

export const draftFeatureKey = 'draft';

export interface IState extends EntityState<IDraft> {
	loading: boolean;
	error?: IResponseError;
	preview?: IDraftPreview;
	lastVerdict?: IDraftVerdictResult;
	filter: IDraftFilter;
}

export const draftAdapter: EntityAdapter<IDraft> = createEntityAdapter<IDraft>({
	selectId: (draft: IDraft) => draft.id,
});

export const initialState: IState = draftAdapter.getInitialState({
	loading: false,
	filter: {
		pagination: {
			count: 0,
			limit: 10,
			offset: 1,
		},
	},
});

export const reducer = createReducer(
	initialState,
	// ── API ──

	on(
		DraftActions.loadDrafts,
		(state): IState => ({
			...state,
			loading: true,
			error: undefined,
		}),
	),
	on(
		DraftActions.loadDraftsSuccess,
		(state, action): IState =>
			draftAdapter.setAll(action.drafts, {
				...state,
				filter: {
					...state.filter,
					pagination: {
						...state.filter.pagination,
						count: action.pagination.count,
					},
				},
				loading: false,
				error: undefined,
			}),
	),
	on(
		DraftActions.loadDraftsError,
		(state, action): IState => ({
			...state,
			loading: false,
			error: action.error,
		}),
	),
	on(
		DraftActions.loadDraftPreview,
		(state): IState => ({
			...state,
			loading: true,
			error: undefined,
			preview: undefined,
		}),
	),
	on(
		DraftActions.loadDraftPreviewSuccess,
		(state, action): IState => ({
			...state,
			loading: false,
			error: undefined,
			preview: action.preview,
		}),
	),
	on(
		DraftActions.loadDraftPreviewError,
		(state, action): IState => ({
			...state,
			loading: false,
			error: action.error,
		}),
	),

	on(
		DraftActions.judgeDraft,
		(state): IState => ({
			...state,
			loading: true,
			lastVerdict: undefined,
		}),
	),
	on(
		DraftActions.judgeDraftSuccess,
		(state, action): IState => ({
			...state,
			loading: false,
			lastVerdict: action.verdict,
		}),
	),
	on(
		DraftActions.judgeDraftError,
		(state): IState => ({
			...state,
			loading: false,
		}),
	),

	// ── UI ──

	on(
		DraftActions.setType,
		(state, action): IState => ({
			...state,
			loading: true,
			error: undefined,
			filter: {
				...state.filter,
				type: action.mediaType,
				pagination: {
					...state.filter.pagination,
					offset: 1,
				},
			},
		}),
	),
	on(
		DraftActions.setOffset,
		(state, action): IState => ({
			...state,
			loading: true,
			filter: {
				...state.filter,
				pagination: {
					...state.filter.pagination,
					offset: action.offset,
				},
			},
		}),
	),
	on(
		DraftActions.setLimit,
		(state, action): IState => ({
			...state,
			loading: true,
			error: undefined,
			filter: {
				...state.filter,
				pagination: {
					...state.filter.pagination,
					offset: 1,
					limit: action.limit,
				},
			},
		}),
	),
	on(
		DraftActions.setSortOrder,
		(state, action): IState => ({
			...state,
			filter: {
				...state.filter,
				sortBy: action.sortBy as IDraftFilter['sortBy'],
				orderBy: action.orderBy,
				pagination: {
					...state.filter.pagination,
					offset: 1,
				},
			},
		}),
	),
	on(
		DraftActions.clearSortOrder,
		(state): IState => ({
			...state,
			filter: {
				...state.filter,
				sortBy: undefined,
				orderBy: undefined,
				pagination: {
					...state.filter.pagination,
					offset: 1,
				},
			},
		}),
	),
	on(
		DraftActions.setIncludeJudged,
		(state, action): IState => ({
			...state,
			loading: true,
			filter: {
				...state.filter,
				includeJudged: action.includeJudged,
				pagination: {
					...state.filter.pagination,
					offset: 1,
				},
			},
		}),
	),
	on(
		DraftActions.clearLastVerdict,
		(state): IState => ({
			...state,
			lastVerdict: undefined,
		}),
	),
	on(
		DraftActions.clearDraftPreview,
		(state): IState => ({
			...state,
			preview: undefined,
		}),
	),
	on(
		DraftActions.clearDraftError,
		(state): IState => ({
			...state,
			error: undefined,
		}),
	),
);
